
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateResponse} from './ConfigUpdateResponse';
import {FieldModelConfigUpdateResponse} from './ConfigUpdateResponse';
import {Manager} from '../core/Manager';
import {FieldModelManager} from '../core/Manager';

/**
 * ManagerCreateResponse struct
 */
class ManagerCreateResponse extends ConfigUpdateResponse {
  /**
   * Initialize struct
   * @param {!ConfigUpdateResponse=} parent
   * @param {!Manager=} manager
   * @param {!string=} password
   * @param {string=} oTPSecret
   * @constructor
   */
  constructor (parent = new ConfigUpdateResponse(), manager = new Manager(), password = '', oTPSecret = null) {
    super()
    super.copy(parent)
    this.Manager = manager
    this.Password = password
    this.OTPSecret = oTPSecret
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ManagerCreateResponse}
   * @param {!ManagerCreateResponse} other Other struct
   * @returns {!ManagerCreateResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Manager != null) {
      this.Manager = Manager.fromObject(other.Manager)
    } else {
      this.Manager = null
    }
    if (other.Password != null) {
      this.Password = other.Password
    } else {
      this.Password = null
    }
    if (other.OTPSecret != null) {
      this.OTPSecret = other.OTPSecret
    } else {
      this.OTPSecret = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ManagerCreateResponse}
   * @returns {!ManagerCreateResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ManagerCreateResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ManagerCreateResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ManagerCreateResponse}
   * @param {!ManagerCreateResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ManagerCreateResponse)) {
      throw new TypeError('Instance of ManagerCreateResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ManagerCreateResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Manager: ((this.Manager != null) ? this.Manager : null),
      Password: ((this.Password != null) ? this.Password : null),
      OTPSecret: ((this.OTPSecret != null) ? this.OTPSecret : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ManagerCreateResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ManagerCreateResponse} other Object value
   * @returns {!ManagerCreateResponse} Created struct
   */
  static fromObject (other) {
    return new ManagerCreateResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ManagerCreateResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ManagerCreateResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ManagerCreateResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6160
  }
}

export { ManagerCreateResponse };

/**
 * Fast Binary Encoding ManagerCreateResponse field model
 */
class FieldModelManagerCreateResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateResponse(buffer, 4 + 4)
    this.Manager = new FieldModelManager(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Password = new fbe.FieldModelString(buffer, this.Manager.fbeOffset + this.Manager.fbeSize)
    this.OTPSecret = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.Password.fbeOffset + this.Password.fbeSize), buffer, this.Password.fbeOffset + this.Password.fbeSize)
  }

  /**
   * Get the ConfigUpdateResponse field model
   * @this {!FieldModelManagerCreateResponse}
   * @returns {!FieldModelConfigUpdateResponse} ConfigUpdateResponse field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelManagerCreateResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelManagerCreateResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Manager.fbeSize + this.Password.fbeSize + this.OTPSecret.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelManagerCreateResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Manager.fbeExtra + this.Password.fbeExtra + this.OTPSecret.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelManagerCreateResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelManagerCreateResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelManagerCreateResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6160
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelManagerCreateResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelManagerCreateResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Manager.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Manager.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Manager.fbeSize

    if ((fbeCurrentSize + this.Password.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Password.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Password.fbeSize

    if ((fbeCurrentSize + this.OTPSecret.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OTPSecret.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OTPSecret.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelManagerCreateResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelManagerCreateResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelManagerCreateResponse}
   * @param {!ManagerCreateResponse} fbeValue Default value, defaults is new ManagerCreateResponse()
   * @returns {!ManagerCreateResponse} ManagerCreateResponse value
   */
  get (fbeValue = new ManagerCreateResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelManagerCreateResponse}
   * @param {!ManagerCreateResponse} fbeValue ManagerCreateResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Manager.fbeSize) <= fbeStructSize) {
      fbeValue.Manager = this.Manager.get()
    } else {
      fbeValue.Manager = new Manager()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Manager.fbeSize

    if ((fbeCurrentSize + this.Password.fbeSize) <= fbeStructSize) {
      fbeValue.Password = this.Password.get()
    } else {
      fbeValue.Password = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Password.fbeSize

    if ((fbeCurrentSize + this.OTPSecret.fbeSize) <= fbeStructSize) {
      fbeValue.OTPSecret = this.OTPSecret.get()
    } else {
      fbeValue.OTPSecret = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OTPSecret.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelManagerCreateResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelManagerCreateResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelManagerCreateResponse}
   * @param {!ManagerCreateResponse} fbeValue ManagerCreateResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelManagerCreateResponse}
   * @param {ManagerCreateResponse} fbeValue ManagerCreateResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Manager.set(fbeValue.Manager)
    this.Password.set(fbeValue.Password)
    this.OTPSecret.set(fbeValue.OTPSecret)
  }
}

export { FieldModelManagerCreateResponse };

/**
 * Fast Binary Encoding ManagerCreateResponse model
 */
class ManagerCreateResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelManagerCreateResponse(this.buffer, 4)
  }

  /**
   * Get the ManagerCreateResponse model
   * @this {!ManagerCreateResponseModel}
   * @returns {!FieldModelManagerCreateResponse} model ManagerCreateResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ManagerCreateResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ManagerCreateResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ManagerCreateResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ManagerCreateResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelManagerCreateResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ManagerCreateResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ManagerCreateResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ManagerCreateResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ManagerCreateResponseModel}
   * @param {!ManagerCreateResponse} value ManagerCreateResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ManagerCreateResponseModel}
   * @param {!ManagerCreateResponse} value ManagerCreateResponse value, defaults is new ManagerCreateResponse()
   * @return {!object} Deserialized ManagerCreateResponse value and its size
   */
  deserialize (value = new ManagerCreateResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ManagerCreateResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ManagerCreateResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ManagerCreateResponseModel}
   * @param {!number} prev Previous ManagerCreateResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ManagerCreateResponseModel };
